'use client';

import { cn } from '@/lib/utils';
import { SHEET_TAB } from '@/lib/copy';

export type CopSheetTab = 'missions' | 'drone' | 'dock' | 'zones';

interface CopSheetTabsProps {
  active: CopSheetTab;
  /** Selected drone's callsign — null disables the DRONE tab (nothing to show). */
  droneLabel: string | null;
  /** Selected dock's callsign — null disables the DOCK tab. */
  dockLabel: string | null;
  /** In-progress missions, shown as a live badge on the MISSIONS tab. */
  liveCount: number;
  /**
   * Whether zone authoring is offered at all, which is the operator's role.
   *
   * A viewer gets three tabs, exactly as before. The tab is not disabled for
   * them, it is absent: an admin control that greys out on a viewer's screen
   * teaches them nothing, and this is a permission rather than a state that can
   * change while they watch (D13's line between the two).
   */
  canAuthorZones: boolean;
  onSelect: (tab: CopSheetTab) => void;
}

/**
 * One right column, four claimants — missions queue, drone workspace, dock
 * sheet, zones — and this strip is how the operator moves between them without
 * losing any of their state. DRONE/DOCK light up only when there is a selection to
 * show: a tab that opens an empty sheet is worse than a disabled one that says
 * why.
 */
export function CopSheetTabs({
  active,
  droneLabel,
  dockLabel,
  liveCount,
  canAuthorZones,
  onSelect,
}: CopSheetTabsProps) {
  const tabs: { key: CopSheetTab; label: string; disabled: boolean; title?: string }[] = [
    { key: 'missions', label: SHEET_TAB.missions, disabled: false },
    {
      key: 'drone',
      label: droneLabel ?? SHEET_TAB.droneFallback,
      disabled: droneLabel === null,
      title: droneLabel === null ? SHEET_TAB.droneDisabled : undefined,
    },
    {
      key: 'dock',
      label: dockLabel ?? SHEET_TAB.dockFallback,
      disabled: dockLabel === null,
      title: dockLabel === null ? SHEET_TAB.dockDisabled : undefined,
    },
    // Last, so the three tabs an operator already reaches for do not move. Zones
    // came off the map's control column in slice 8; the COP is still the only
    // place in the product where a geofence can be authored, because
    // `(dashboard)/geofences/page.tsx` is a bare redirect back to here.
    ...(canAuthorZones
      ? [{ key: 'zones' as const, label: SHEET_TAB.zones, disabled: false }]
      : []),
  ];

  return (
    <div className="flex items-stretch border-b divider bg-card/60">
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            disabled={tab.disabled}
            title={tab.title}
            aria-current={isActive ? 'true' : undefined}
            onClick={() => onSelect(tab.key)}
            className={cn(
              'flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2',
              'text-[10px] uppercase tracking-wider border-b-2 -mb-px transition-colors',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              tab.disabled && 'opacity-40 cursor-not-allowed hover:text-muted-foreground',
            )}
          >
            <span className="truncate">{tab.label}</span>
            {tab.key === 'missions' && liveCount > 0 && (
              <span className="shrink-0 rounded bg-status-green/15 border border-status-green/40 px-1 text-status-green-fg tabular-nums">
                {liveCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
